import store from './store/index'
import { getOtpNumber } from './api/waiting'
import moment from 'moment'
import otp from 'speakeasy'

// 숫자 콤마찍기
export function numberWidthComma (num, limit = -1) {
  if (limit !== -1) {
    num = String(num)
    num = (num.length > limit) ? num.substr(0, num.length - 1) : num
  }
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * @description locale 값을 서버의 multiLanguage 키로 매핑
 * @param {string} locale - 'ko', 'en', 'jp', 'cn_simplified'
 * @returns {string|undefined} - 'en', 'ja', 'zh-CN' or undefined
 */
export function getMultiLanguageKey (locale) {
  const localeMap = {
    jp: 'ja',
    cn_simplified: 'zh-CN',
    en: 'en'
  }
  return localeMap[locale]
}

/**
 * @description 다국어 이름 가져오기 (queue, request 등)
 * @param {Object} item - queue 또는 request 객체
 * @param {string} locale - 현재 locale
 * @returns {string} - 다국어 이름 또는 기본 이름
 */
export function getMultiLanguageName (item, locale) {
  if (!item || !item.name) return ''

  // 한국어인 경우 기본 이름 반환
  if (locale === 'ko') {
    return item.name
  }

  const multiLanguageKey = getMultiLanguageKey(locale)

  // 다국어 정보가 있고 해당 언어가 존재하는 경우
  if (multiLanguageKey && item.multiLanguage && item.multiLanguage.name && item.multiLanguage.name[multiLanguageKey]) {
    return item.multiLanguage.name[multiLanguageKey]
  }

  // 기본값 반환
  return item.name
}

// OTP 갱신 step(초). 호출 스크린(WD)·서버와 일치해야 같은 코드가 표시된다.
const OTP_STEP_SECONDS = 3600
const OTP_POLL_INTERVAL_MS = 1000

// 특정 매장은 성인/유아 명칭을 커스텀 텍스트로 표기
// TB-14228: 오이탈리안 (성인식기세팅/유아식기세팅)
// TB-14471: 김삿갓밥집 (일반 (초2 부터)/소인 미만 (초2 미만))
// 대상 매장이 아니면 null 반환 (기본 i18n 명칭 사용)
export function getCustomPersonLabel (storeIdx) {
  if (process.env.VUE_APP_CUSTOM_PERSON_LABEL_2 && process.env.VUE_APP_CUSTOM_PERSON_LABEL_2.includes(storeIdx)) {
    return { adult: '일반 (초2 부터)', child: '소인 미만 (초2 미만)' }
  }
  if (process.env.VUE_APP_CUSTOM_PERSON_LABEL && process.env.VUE_APP_CUSTOM_PERSON_LABEL.includes(storeIdx)) {
    return { adult: '성인식기세팅', child: '유아식기세팅' }
  }
  return null
}

// 대기 확정 코드 생성
export function exce (idx) {
  const secret = `Tabling!!_${idx}`
  return otp.totp({ secret, time: moment().unix(), step: OTP_STEP_SECONDS, algorithm: 'sha256' })
}

export function renewalOtpNumber () {
  clearInterval(store.state.timeoutObj)
  let lastStep = null

  // 매 tick마다 현재 step 구간을 비교해 구간이 바뀐 경우에만 재생성한다.
  // 한 번 실패해도 다음 tick에서 재시도되므로 갱신 체인이 끊기지 않는다.
  const tick = async () => {
    const step = Math.floor(moment().unix() / OTP_STEP_SECONDS)
    if (step === lastStep) {
      return
    }
    try { // OTP 로컬에서 생성
      const localOtp = exce(store.state.restaurant.idx)
      store.commit('otpNumber', localOtp)
      lastStep = step
      console.debug('renewalOtpNumber---', localOtp, new Date())
    } catch (error) { // 로컬 생성 실패시 서버요청으로 폴백
      if (!store.getters.getAccessToken) {
        console.error('renewalOtpNumber local error---', error)
        return
      }
      try {
        const { data } = await getOtpNumber(store.getters.getStoreIdx)
        store.commit('otpNumber', data.token)
        lastStep = step
        console.debug('renewalOtpNumber(server)---', data.token, new Date())
      } catch (serverError) {
        console.error('renewalOtpNumber error---', error, serverError)
      }
    }
  }

  tick()
  store.commit('timeoutObj', setInterval(tick, OTP_POLL_INTERVAL_MS))
}

// 특정 매장은 최대 인원 초과 시 기본 안내 문구(ask_staff) 대신 커스텀 문구를 노출
// TB-14651: 다담석갈비 (다른 번호로 분리 접수 안내, 매장 부착 안내문 문구 그대로 사용)
// 대상 매장이 아니면 null 반환 (기본 i18n 문구 사용)
export function getCustomMaxPersonMsg (storeIdx) {
  const targets = (process.env.VUE_APP_CUSTOM_MAX_PERSON_MSG || '').split(',')
  if (!storeIdx || !targets.includes(storeIdx.toString())) {
    return null
  }
  // 예시 문단은 본문보다 작고 얇게, 경고 문단은 red로 강조한다.
  // AlertPopup의 .alert_msg는 v-html이라 scoped style이 적용되지 않아 inline style을 사용한다.
  const exampleStyle =
    'font-weight:400;font-size:32px;font-size:min(2.5vw, 32px)'
  // .alert_msg는 display:flex(row)라 자식이 가로로 배치되므로
  // 문단이 세로로 쌓이도록 전체를 하나의 컨테이너로 감싼다.
  return (
    '<div>' +
    '<div>4명 이상일 경우 서로 다른 전화번호로 각각 등록해주세요.</div>' +
    `<div style="margin-top:10px;${exampleStyle}">(예) 6명일 경우</div>` +
    `<div style="${exampleStyle}">010-1111-1111 3명 등록 / 010-2222-2222 3명 등록</div>` +
    `<div style="${exampleStyle}">(각각 다른 번호로 등록하셔야 합니다.)</div>` +
    '<div style="margin-top:10px;color:#D81B1B">4명 이상일 경우 같은 테이블에 앉을 수 없습니다.</div>' +
    '</div>'
  )
}
